import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { VitalParameters } from 'types/result';
import {
  VITAL_PARAMETER_KEYS,
  type VitalParameterKey,
} from 'widgets/results/VitalParametersSection';
import type { EditorAutosaveDeps } from 'hooks/useAutosaveStatus';

const EMPTY_SELECTION_MAP: Record<VitalParameterKey, boolean> = {
  systolicMmhg: false,
  diastolicMmhg: false,
  pulseBpm: false,
  spo2Percent: false,
  temperatureC: false,
  respiratoryRateBpm: false,
  weightKg: false,
  heightCm: false,
};

export interface UseVitalParametersEditorProps extends EditorAutosaveDeps {
  resultId: string | number | undefined;
  vitalParameters: VitalParameters | undefined;
}

export interface UseVitalParametersEditorReturn {
  currentVitalParameters: VitalParameters;
  vitalParameterSelectionMap: Record<VitalParameterKey, boolean>;
  hasDetectedVitalParameters: boolean;
  handleVitalParameterChange: (key: VitalParameterKey, value: string) => void;
  handleVitalParameterSelectionToggle: (key: VitalParameterKey, checked: boolean) => void;
  pendingVitalParameters: () => VitalParameters | null;
  clearPendingVitalParameters: () => void;
}

export function useVitalParametersEditor({
  resultId,
  vitalParameters,
  autosaveEditAsync,
  autosaveTimeouts,
  setAutoStatus,
  setHasUserEdits,
}: UseVitalParametersEditorProps): UseVitalParametersEditorReturn {
  const [editedVitalParameters, setEditedVitalParameters] = useState<Partial<VitalParameters>>({});
  const [vitalParameterSelectionMap, setVitalParameterSelectionMap] =
    useState<Record<VitalParameterKey, boolean>>(EMPTY_SELECTION_MAP);
  const hasPendingVitalEditsRef = useRef(false);

  useEffect(() => {
    setEditedVitalParameters({});
    setVitalParameterSelectionMap(EMPTY_SELECTION_MAP);
    hasPendingVitalEditsRef.current = false;
  }, [resultId]);

  useEffect(() => {
    if (vitalParameters) {
      setVitalParameterSelectionMap((prev) => {
        // Select vital parameters with values by default (if not already selected)
        const updates = VITAL_PARAMETER_KEYS.reduce<Record<string, boolean>>((acc, key) => {
          const value = vitalParameters[key];
          if (value != null && !prev[key]) {
            acc[key] = true;
          }
          return acc;
        }, {});
        return { ...prev, ...updates };
      });
    }
  }, [vitalParameters]);

  const handleVitalParameterChange = useCallback(
    (key: VitalParameterKey, value: string) => {
      setEditedVitalParameters((prev) => {
        return { ...prev, [key]: value };
      });
      hasPendingVitalEditsRef.current = true;
      setHasUserEdits(true);
      setAutoStatus((prev) => ({ ...prev, [key]: 'typing' }));

      // Auto-select logic
      if (value && !vitalParameterSelectionMap[key]) {
        setVitalParameterSelectionMap((prev) => ({ ...prev, [key]: true }));
      } else if (!value && vitalParameterSelectionMap[key]) {
        setVitalParameterSelectionMap((prev) => ({ ...prev, [key]: false }));
      }

      clearTimeout(autosaveTimeouts.current[key]);
      autosaveTimeouts.current[key] = setTimeout(() => {
        setAutoStatus((prev) => ({ ...prev, [key]: 'saving' }));
        const currentVitals = vitalParameters ?? {};
        const merged = { ...currentVitals, ...editedVitalParameters, [key]: value };
        void autosaveEditAsync({ vitalParameters: merged })
          .then(() => setAutoStatus((prev) => ({ ...prev, [key]: 'saved' })))
          .catch(() => setAutoStatus((prev) => ({ ...prev, [key]: 'error' })));
      }, 1000);
    },
    [
      vitalParameterSelectionMap,
      vitalParameters,
      editedVitalParameters,
      autosaveTimeouts,
      autosaveEditAsync,
      setAutoStatus,
      setHasUserEdits,
    ]
  );

  const handleVitalParameterSelectionToggle = useCallback(
    (key: VitalParameterKey, checked: boolean) => {
      setVitalParameterSelectionMap((prev) => ({ ...prev, [key]: checked }));
      setHasUserEdits(true);
    },
    [setHasUserEdits]
  );

  const currentVitalParameters: VitalParameters = useMemo(
    () => ({
      ...(vitalParameters ?? {}),
      ...editedVitalParameters,
    }),
    [vitalParameters, editedVitalParameters]
  );

  const hasDetectedVitalParameters = vitalParameters
    ? VITAL_PARAMETER_KEYS.some((key) => vitalParameters[key] != null)
    : false;

  const pendingVitalParameters = useCallback((): VitalParameters | null => {
    if (!hasPendingVitalEditsRef.current) return null;
    VITAL_PARAMETER_KEYS.forEach((key) => {
      clearTimeout(autosaveTimeouts.current[key]);
      delete autosaveTimeouts.current[key];
    });
    return currentVitalParameters;
  }, [autosaveTimeouts, currentVitalParameters]);

  const clearPendingVitalParameters = useCallback(() => {
    hasPendingVitalEditsRef.current = false;
  }, []);

  return useMemo(
    () => ({
      currentVitalParameters,
      vitalParameterSelectionMap,
      hasDetectedVitalParameters,
      handleVitalParameterChange,
      handleVitalParameterSelectionToggle,
      pendingVitalParameters,
      clearPendingVitalParameters,
    }),
    [
      currentVitalParameters,
      vitalParameterSelectionMap,
      hasDetectedVitalParameters,
      handleVitalParameterChange,
      handleVitalParameterSelectionToggle,
      pendingVitalParameters,
      clearPendingVitalParameters,
    ]
  );
}
